import Cookies from "js-cookie";
import {
  SWEETENER_COUPON_OFFER_COOKIE_NAME,
  SWEETENER_COUPON_OFFER_COOKIE_VERSION,
  type SweetenerCouponOfferCookiePayload,
} from "~/constants/sweetenerCouponOfferCookie";
import clientSide from "~/helpers/clientSide";
import { setCookie } from "~/helpers/cookie";
import { hasMarketingConsent } from "~/helpers/returnVisitorCookie";
import { getSamsungBestBuySweetener } from "~/helpers/samsungOffer/getSamsungBestBuySweetener";
import type { Product } from "~/types/products";

export function getSweetenerCouponOfferCookie(): SweetenerCouponOfferCookiePayload | null {
  if (!clientSide || !hasMarketingConsent()) {
    return null;
  }
  try {
    const raw = Cookies.get(SWEETENER_COUPON_OFFER_COOKIE_NAME);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as SweetenerCouponOfferCookiePayload;
    if (parsed?.version !== SWEETENER_COUPON_OFFER_COOKIE_VERSION || typeof parsed.quoteId !== "string") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * After successful `products/get`: records whether any returned broadband product carries the Samsung COUPON sweetener.
 * Resets `selected` when `quoteId` changes or promo is absent.
 */
export function syncSweetenerCouponOfferCookieAfterGetProducts(
  quoteId: string | undefined,
  availableProducts: Product[] | undefined
): void {
  if (!clientSide || !hasMarketingConsent()) {
    return;
  }
  if (!quoteId || !availableProducts?.length) {
    return;
  }

  const couponAvailable = availableProducts.some((p) => getSamsungBestBuySweetener(p.sweeteners) != null);

  const existing = getSweetenerCouponOfferCookie();
  const quoteChanged = Boolean(existing && existing.quoteId !== quoteId);

  const next: SweetenerCouponOfferCookiePayload = {
    version: SWEETENER_COUPON_OFFER_COOKIE_VERSION,
    quoteId,
    available: couponAvailable,
    selected: couponAvailable && !quoteChanged ? Boolean(existing?.selected) : false,
  };

  try {
    setCookie(SWEETENER_COUPON_OFFER_COOKIE_NAME, JSON.stringify(next), { otGroup: "C0003" });
  } catch (e) {
    console.error("Failed to set sweetenerCouponOffer cookie", e);
  }
}

/**
 * When the shopper selects a broadband plan that includes the Samsung coupon sweetener, mark selection in cookie.
 */
export function markSweetenerCouponOfferSelectedFromBroadbandProduct(product: Product | undefined): void {
  if (!clientSide || !hasMarketingConsent()) {
    return;
  }
  if (!getSamsungBestBuySweetener(product?.sweeteners)) {
    return;
  }

  const quoteId = Cookies.get("quoteId");
  if (!quoteId) {
    return;
  }

  const existing = getSweetenerCouponOfferCookie();
  if (!existing || existing.quoteId !== quoteId || !existing.available) {
    return;
  }

  const next: SweetenerCouponOfferCookiePayload = {
    ...existing,
    selected: true,
  };

  try {
    setCookie(SWEETENER_COUPON_OFFER_COOKIE_NAME, JSON.stringify(next), { otGroup: "C0003" });
  } catch (e) {
    console.error("Failed to update sweetenerCouponOffer cookie", e);
  }
}
